import common from '@/pages/adminPanel/common.module.scss'
import {useNavigate, useParams} from "react-router-dom";
import {FaArrowLeft} from "react-icons/fa";
import React, {useEffect, useMemo, useRef, useState} from "react";
import styles from './PromoForm.module.css'
import {createPromo, getPromo, updatePromo} from "@/pages/adminPanel/Components/promo/api/promoApi.js";
import {searchUsers} from "@/pages/adminPanel/api/adminApi.js";
import {toast} from "react-toastify";

export default function PromoForm () {
    const {id} = useParams();
    const isEdit = Boolean(id);
    const nav = useNavigate();

    // form
    const [name, setName] = useState('');
    const [code, setCode] = useState('');
    const [discount, setDiscount] = useState(''); // храним как строку; конвертим на сабмите
    const [selectedType, setSelectedType] = useState('DEFAULT');
    const [selectedUserId, setSelectedUserId] = useState(null);
    const [user, setUser] = useState(null);

    // search
    const [searchTerm, setSearchTerm] = useState('');
    const [searchResults, setSearchResults] = useState([]);

    // loading flags
    const [loading, setLoading] = useState(false);
    const [loadingPromo, setLoadingPromo] = useState(false);

    // защита от гонок при поиске
    const requestIdRef = useRef(0);

    const PROMO_TYPES = useMemo(() => ([
        { value: 'DEFAULT',    label: 'Обычный' },
        { value: 'USER',       label: 'Персональный' },
        { value: 'BIRTHDAY',   label: 'День рождения' },
        { value: 'FIRST',      label: 'Первая игра' },
        { value: 'INVITATION', label: 'Приведи друзей' },
    ]), []);

    // Загрузка промо в режиме редактирования
    useEffect(() => {
        const fetchData = async () => {
            if (!isEdit) return;
            setLoadingPromo(true);
            try {
                const res = await getPromo(id);
                const data = res.data;

                setName(data.name ?? '');
                setCode(data.code ?? '');
                setDiscount(String(data.discountPercent ?? ''));
                setSelectedType(data.promoType ?? 'DEFAULT');

                if (data.siteUser) {
                    setSelectedUserId(data.siteUser.id);
                    setUser(data.siteUser);
                } else {
                    setSelectedUserId(null);
                    setUser(null);
                }
            } catch (e) {
                const errorMessage = e?.response?.data?.message || 'Ошибка загрузки промокода';
                toast.error(errorMessage);
                console.error('Ошибка загрузки промокода', e);
            } finally {
                setLoadingPromo(false);
            }
        };

        fetchData();
    }, [id, isEdit]);

    // Если тип не USER — чистим выбранного пользователя и поисковую строку/результаты
    useEffect(() => {
        if (selectedType !== 'USER') {
            setSelectedUserId(null);
            setUser(null);
            setSearchTerm('');
            setSearchResults([]);
        }
    }, [selectedType]);

    // Дебаунс-поиск пользователей (только для USER), с защитой от гонок
    useEffect(() => {
        if (selectedType !== 'USER') return;

        const term = searchTerm.trim();
        if (!term) {
            setSearchResults([]);
            return;
        }

        const currentRequestId = ++requestIdRef.current;
        const timer = setTimeout(async () => {
            try {
                const res = await searchUsers(term);
                // обновляем только если это всё ещё актуальный запрос
                if (currentRequestId === requestIdRef.current) {
                    setSearchResults(Array.isArray(res.data) ? res.data : []);
                }
            } catch (e) {
                // если это не отмена и не гонка — покажем ошибку
                if (currentRequestId === requestIdRef.current) {
                    const errorMessage = e?.response?.data?.message || 'Ошибка при поиске пользователя';
                    toast.error(errorMessage);
                }
            }
        }, 300);

        return () => clearTimeout(timer);
    }, [searchTerm, selectedType]);

    const handleSubmit = async (e) => {
        e.preventDefault();

        // валидация
        const trimmedName = name.trim();
        const normalizedCode = code.trim();
        if (!trimmedName || !normalizedCode) {
            toast.error('Заполните название и код');
            return;
        }

        const discountNum = Number(discount);
        if (Number.isNaN(discountNum) || discountNum < 0 || discountNum > 100) {
            toast.error('Размер скидки должен быть числом от 0 до 100');
            return;
        }

        if (selectedType === 'USER' && !selectedUserId) {
            toast.error('Выберите пользователя для персонального промокода');
            return;
        }

        setLoading(true);
        try {
            if (isEdit) {
                await updatePromo(id, trimmedName, normalizedCode, discountNum, selectedType, selectedUserId);
            } else {
                await createPromo(trimmedName, normalizedCode, discountNum, selectedType, selectedUserId);
            }
            toast.success('Промокод сохранён');
            nav('/admin/');
        } catch (error) {
            toast.error(error?.response?.data?.message || "Ошибка сохранения промокода");
        } finally {
            setLoading(false);
        }
    };

    // обработчики инпутов
    const handleCodeChange = (e) => {
        const val = e.target.value.toUpperCase().replace(/\s+/g, '');
        setCode(val);
    };

    const handleUserClick = (u) => {
        setSelectedUserId(prev => prev === u.id ? null : u.id);
        setUser(prev => (prev && prev.id === u.id) ? null : u);
    };

    return (
        <div className={`container ${common.container}`}>
            <div className={`row align-items-center`}>
        <span className="btn-word black btn-link col-md-auto col-2" onClick={() => nav(-1)}>
          <FaArrowLeft className={common.serviceButton}/>
        </span>
                <h2 className="col-md-auto col-10">{isEdit ? 'Редактирование промокода' : 'Создание промокода'}</h2>

                <form className={`mt-3`} onSubmit={handleSubmit}>
                    {/* Блокируем всю форму на время загрузки/сохранения */}
                    <fieldset disabled={loadingPromo || loading} style={{ border: 'none', padding: 0, margin: 0 }}>
                        <div className="row">
                            <div className={`col-md-4`}>
                                <div className={`mb-3`}>
                                    <label htmlFor="name" className={`${styles.label} mb-1`}>Название</label>
                                    <input
                                        className="form-control"
                                        value={name}
                                        type="text"
                                        name="name"
                                        id="name"
                                        onChange={(e) => setName(e.target.value)}
                                    />
                                </div>

                                <div className={`mb-3`}>
                                    <label htmlFor="code" className={`${styles.label} mb-1`}>Код</label>
                                    <input
                                        className="form-control"
                                        value={code}
                                        type="text"
                                        name="code"
                                        id="code"
                                        onChange={handleCodeChange}
                                    />
                                </div>

                                <div className={`mb-3`}>
                                    <label htmlFor="discount" className={`${styles.label} mb-1`}>Размер скидки</label>
                                    <input
                                        className="form-control"
                                        value={discount}
                                        type="number"
                                        name="discount"
                                        id="discount"
                                        min={0}
                                        max={100}
                                        step={1}
                                        onChange={(e) => setDiscount(e.target.value)}
                                    />
                                </div>
                            </div>

                            <div className={`col-md-4`}>
                                <h4 className={`mb-2`}>Тип промокода</h4>
                                <div className={`d-flex flex-column gap-2`}>
                                    {PROMO_TYPES.map(t => (
                                        <label key={t.value} className={`form-control`}>
                                            <input
                                                className={`me-1`}
                                                type="radio"
                                                name="type"
                                                value={t.value}
                                                onChange={(e) => setSelectedType(e.target.value)}
                                                checked={selectedType === t.value}
                                            />
                                            {t.label}
                                        </label>
                                    ))}
                                </div>
                            </div>

                            <div className={`col-md-4 mt-4`}>
                                <div className="input-group mb-3">
                                    <input
                                        type="text"
                                        className="form-control"
                                        placeholder={selectedType === 'USER' ? 'Поиск' : 'Доступно только для персональных кодов'}
                                        value={searchTerm}
                                        disabled={selectedType !== 'USER'}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        onKeyDown={(e) => { if (e.key === 'Enter') e.preventDefault(); }}
                                    />
                                </div>

                                {selectedUserId && user && (
                                    <p className="mb-2">Пользователь: {user?.fio} | ID: {user?.id}</p>
                                )}

                                <ul className={common.modalList + " list-group mb-3"} role="listbox" aria-disabled={selectedType !== 'USER'}>
                                    {selectedType === 'USER' && searchTerm.trim() && searchResults.length === 0 && (
                                        <li className="list-group-item text-muted">Ничего не найдено</li>
                                    )}

                                    {selectedType === 'USER' && searchResults.map(u => (
                                        <li
                                            key={u.id}
                                            role="option"
                                            aria-selected={selectedUserId === u.id}
                                            className={`list-group-item ${selectedUserId === u.id ? 'active' : ''}`}
                                            onClick={() => handleUserClick(u)}
                                            style={{ cursor: 'pointer' }}
                                        >
                                            {u.fio} | {u.phone}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>

                        <button className={`service-btn col-md-4 mt-5`} disabled={loading || loadingPromo}>
                            {loading ? 'Сохранение...' : 'Сохранить промокод'}
                        </button>
                    </fieldset>
                </form>
            </div>
        </div>
    );
}
